import React from "react";
import PropTypes from "prop-types";
import Typography from "@material-ui/core/Typography";
import { observer } from "mobx-react";
import { compose } from "recompose";
import Paper from "@material-ui/core/Paper";
import { MuiThemeProvider } from "@material-ui/core/styles";
import { withStyles } from "@material-ui/core/styles";
import aceTheme from "../../util/aceTheme";
import LinearProgress from "@material-ui/core/LinearProgress";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import _ from "lodash";

// Components
import StickyTable from "../stickyTable/StickyTable";
import ChaseNote from "./ChaseNote";
import SMSDialog from "./SMSDialog";
import BulSMSDialog from "./BulkSMSDialog";

// Store
import store from "./Store";

const styles = {
  paper: {
    paddingTop: 20,
  },
  chaseTable: {
    padding: 0,
    margin: 0,
    boxShadow: "3px 3px 3px #bbb",
    width: "100%",
  },
  gameHeader: {
    padding: 0,
    margin: 0,
    color: "#FFF",
    backgroundColor: "#06b",
    fontSize: 14,
    fontWeight: "bold",
  },
  gameHeaderData: {
    padding: "4px 10px",
  },
  gameHeaderMain: {
    fontSize: 16,
    backgroundColor: "#06b",
    color: "#fff",
    fontWeight: "bold",
  },
  gameHeaderMainHeader: {
    padding: "4px 10px",
  },
  rowGame: {
    fontSize: 15,
    color: "#02243C",
  },
  rowGameEven: {
    backgroundColor: "#EEE",
  },
  rowGameOdd: {
    backgroundColor: "#FFF",
  },
  rowGameData: {
    padding: 4,
    textAlign: "center",
    borderRight: "1px solid #DDD",
    borderBottom: "1px solid #DDD",
  },
  progressLinear: {
    width: "100%",
    height: 5,
  },
};

const stylesMaterial = (theme) => ({
  paperClass: {
    margin: "25px 0",
    padding: "10px 5px",
  },
  smallCell: { padding: 0, width: 55, textAlign: "center" },
  smallCellNumber: { padding: 0, width: 55, textAlign: "center" },
  smallCellMark: { fontSize: 12, padding: 0, width: 35, textAlign: "center" },
  smallCellEnabled: { width: 100, textAlign: "center", padding: 0 },
  table: { overflow: "visible" },
  playerTotal: { color: "#888", fontSize: "0.8em" },
});

const isMobile = window.matchMedia("(max-device-width: 960px)").matches;
if (isMobile) {
  styles.gameHeaderMainHeader.fontSize = 40;
  styles.gameHeaderData.fontSize = 50;
  styles.rowGameData.fontSize = 35;
}

const renderChaseRow = (
  chaseList,
  onClear,
  onSave,
  columnDimensions,
  toggleDialog
) =>
  _.map(chaseList, (chaseObj, index) => {
    return (
      <tr
        key={`chase-list-row-${index}`}
        style={{
          ...styles.rowGame,
          backgroundColor: index % 2 ? "#EEE" : "#FFF",
        }}
      >
        <td
          style={{
            ...styles.rowGameData,
            width: columnDimensions.selectedWidth,
            textAlign: "center",
          }}
        >
          <Checkbox
            checked={chaseObj.selected}
            onChange={() => store.toggleCheckbox(index)}
            value={chaseObj.IdAgent}
          />
        </td>
        <td
          style={{
            ...styles.rowGameData,
            width: columnDimensions.agentWidth,
            textAlign: "center",
          }}
        >
          {chaseObj.Agent}
        </td>
        <td
          style={{
            ...styles.rowGameData,
            width: columnDimensions.balanceWidth,
            textAlign: "center",
          }}
        >
          {chaseObj.due === 0
            ? `${chaseObj.due.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`
            : `-${chaseObj.due.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`}
        </td>
        <td
          style={{
            ...styles.rowGameData,
            width: columnDimensions.notesWidth,
          }}
        >
          <ChaseNote
            {...{
              isMobile,
              chaseObj,
              onSave,
              onClear,
              toggleDialog: () => {
                store.toggleDialog(chaseObj);
              },
            }}
          />
        </td>
      </tr>
    );
  });

@observer
class ChaseTableList extends React.Component {
  state = {
    columnDimensions: {
      agentWidth: 0,
      balanceWidth: 0,
      notesWidth: 0,
      selectedWidth: 0,
    },
  };

  componentDidMount() {
    const { prmIdAgent } = this.props;
    store.getChaseList(prmIdAgent);
  }

  render() {
    const { title, onClear, onSave, classes } = this.props;
    const contentDimensions = document.getElementById("content");
    let containerWidth = 0;
    let columnDimensions = {};
    if (contentDimensions) {
      const clientRectContent = contentDimensions.getBoundingClientRect();
      containerWidth = clientRectContent.width / 100;
      columnDimensions = {
        agentWidth: isMobile ? containerWidth * 30 : containerWidth * 15,
        selectedWidth: isMobile ? containerWidth * 15 : containerWidth * 7.5,
        balanceWidth: isMobile ? containerWidth * 15 : containerWidth * 7.5,
        notesWidth: isMobile ? containerWidth * 40 : containerWidth * 70,
      };
    }
    const disableBulkSMS =
      _.size(
        _.filter(
          store.chaseList,
          (item) => item.Phone !== "" && item.selected !== false
        )
      ) > 0;
    const disableResetChecks =
      _.size(_.filter(store.chaseList, (item) => item.bulkSMS !== false)) > 0;

    return (
      <MuiThemeProvider theme={aceTheme}>
        <BulSMSDialog
          {...{
            isOpen: store.isBulkOpen,
            bulkTextbox: store.bulkTextbox,
            timeInterval: store.timeInterval,
            totalBulkMessagesSent: store.totalBulkMessagesSent,
            bulkInProgress: store.bulkInProgress,
            currentSendingAgent: store.currentSendingAgent,
            totalBulkMessages: store.totalBulkMessages,
            handleClose: () => store.toggleBulkDialog(),
            sendSMSBulk: () => store.sendSMSBulk(),
            setBulkMessage: (msg) => store.setBulkMessage(msg),
            setTimeInterval: (time) => store.setTimeInterval(time),
          }}
        />
        <SMSDialog
          {...{
            isOpen: store.isDialogOpen,
            chaseObj: store.chaseObj,
            phones: store.phones,
            defaultPhone: store.defaultPhone,
            textbox: store.textbox,
            feedbackMsg: store.feedbackMsg,
            addPhoneOpen: store.addPhoneOpen,
            phoneToAdd: store.phoneToAdd,
            handleClose: () => store.toggleDialog(),
            setMessage: (message) => store.setMessage(message),
            sendSMS: () => store.sendSMS(),
            setFeedback: (msg) => store.setFeedback(msg),
            addPhoneNumber: (chaseObject) => store.addPhoneNumber(chaseObject),
            toggleAddNumber: () => store.toggleAddNumber(),
            setDefaultPhone: (phone, fromSelect) =>
              store.setDefaultPhone(phone, fromSelect),
          }}
        />
        <Paper className={classes.paperClass}>
          {_.size(store.chaseList) > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: 10,
              }}
            >
              <div>
                <Typography
                  className="player-management-table-title"
                  align="center"
                  variant="display1"
                  gutterBottom
                >
                  {_.size(store.chaseList)} agents owe{" "}
                  {store.totalChase !== 0 &&
                    store.totalChase
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, "$&,")}{" "}
                  {title}
                </Typography>
              </div>
              <div>
                <Button
                  {...{
                    variant: "contained",
                    color: "primary",
                    className: classes.button,
                    style: {
                      marginRight: 10,
                      minWidth: 144,
                    },
                    disabled: !disableBulkSMS,
                    onClick: () => store.toggleBulkDialog(),
                  }}
                >
                  BULK SMS
                </Button>
                <Button
                  disabled={!disableResetChecks}
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={() => store.resetBulkChecks(this.props.prmIdAgent)}
                  style={{ marginRight: 10, minWidth: 144 }}
                >
                  RESET CHECKS
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={() => store.selectAll()}
                  style={{ minWidth: 144 }}
                >
                  {!store.selectAllProp ? "SELECT ALL" : "UNSELECT ALL"}
                </Button>
              </div>
            </div>
          )}
          {_.size(store.chaseList) === 0 && (
            <LinearProgress className={classes.progressLinear} />
          )}
          {_.size(store.chaseList) > 0 && (
            <StickyTable
              {...{
                tableId: "chase-list",
                upperElementClass: "reportHeader",
                headerRows: [
                  <tr style={styles.gameHeaderMain}>
                    <th
                      style={{
                        ...styles.gameHeaderMainHeader,
                        width: columnDimensions.selectedWidth,
                        cursor: "pointer",
                      }}
                      onClick={() => store.selectAll()}
                    >
                      Selected
                    </th>
                    <th
                      style={{
                        ...styles.gameHeaderMainHeader,
                        width: columnDimensions.agentWidth,
                      }}
                    >
                      Agent
                    </th>
                    <th
                      style={{
                        ...styles.gameHeaderMainHeader,
                        width: columnDimensions.balanceWidth,
                      }}
                    >
                      Balance
                    </th>
                    <th
                      style={{
                        ...styles.gameHeaderMainHeader,
                        width: columnDimensions.notesWidth,
                        textAlign: "left",
                      }}
                    >
                      Notes
                    </th>
                  </tr>,
                ],
                tableBody: (
                  <tbody>
                    {_.size(store.chaseList) > 0 &&
                      renderChaseRow(
                        store.chaseList,
                        onClear,
                        onSave,
                        columnDimensions,
                        store.toggleDialog
                      )}
                  </tbody>
                ),
              }}
            />
          )}
        </Paper>
      </MuiThemeProvider>
    );
  }
}

ChaseTableList.propTypes = {
  title: PropTypes.string.isRequired,
  prmIdAgent: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onFail: PropTypes.func.isRequired,
};

ChaseTableList.defaultProps = {
  title: "Sample Table",
};

export default compose(
  withStyles(stylesMaterial, {
    name: "ChaseTableList",
    withTheme: true,
  })
)(ChaseTableList);
